import {
  Header,
  HeaderItem,
  Sidebar,
  SidebarFooter,
  SidebarItem,
  SidebarPageWrapper,
  SidebarTab,
  SidebarTabLabel,
  SidebarTabs,
} from '@trueprodigy/components';
import * as L from 'lodash';
import PropTypes from 'prop-types';
import React, { Fragment, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { compose, lifecycle, withStateHandlers } from 'recompose';
import styled from 'styled-components';

import PropertyLookupModal from '@modules/appraisal/components/propertyLookupModal';
import ArbitratorLookupModal from '@modules/litigation/components/arbitratorLookupModal';
import ArbitratorCreateModal from '@modules/litigation/components/arbitratorCreateModal';
import OwnerLookupModal from '@modules/appraisal/components/ownerLookupModal';
import OwnerCreateModal from '@modules/appraisal/components/ownerCreateModal';

import ability, { defineRules } from '@config/permissions/ability';

import * as globalActions from '@actions/global';
import HeaderZendeskWidget from '@components/HeaderZendeskWidget/HeaderZendeskWidget';
import rocketIcon from '@components/Sidebar/assets/rocket-icon.png';
import SidebarAccountSettingsWidget from '@components/Sidebar/SidebarAccountSettingsWidget';
import SidebarCaseSettingWidget from '@components/Sidebar/SidebarCaseSettingWidget';
import SidebarRdsNameWidget from '@components/Sidebar/SidebarRdsNameWidget';
import HeaderWrapper from '@components/HeaderWrapper';
import QuickAccessCase from './quickAccessCase';
import {
  isSidebarVisibleSelector,
  userRightsSelector,
  userStatusSelector,
} from '@selectors/global';

import docIcon from '../assets/audit-trail-icon.png';
import generateIcon from '../assets/generate-icon.png';
import litigationLogo from '../assets/litigation-app-logo.png';
import partyIcon from '../assets/party-icon.png';
import screenIcon from '../assets/print-screen-icon.png';

import * as caseActions from '../actions/case';
import * as arbitratorActions from '../actions/arbitrator';
import * as arbitrationActions from '../actions/arbitration';
import { LitigationPageTitle, LitigationRoute } from '../routes';
import { activeCaseSelector } from '../selectors/case';
import { activeArbitrationSelector } from '@modules/litigation/selectors/arbitration';
import { fetchCurrentAppraisalYear } from '@modules/appraisal/actions/global';
import { activeArbitratorSelector } from '@modules/litigation/selectors/arbitrator';
import { canViewSubject } from '@config/permissions/Can';

const parseHeaderTitle = pathname => {
  const isCreate = pathname.match(/create/g);
  const idPattern = pathname.match(/[0-9]+/g);

  if (L.isNil(idPattern)) {
    return LitigationPageTitle[pathname];
  }

  if (isCreate) {
    return LitigationPageTitle[pathname.replace(idPattern[0], ':caseId')];
  }

  return idPattern.length === 1
    ? LitigationPageTitle[pathname.replace(idPattern[0], ':id')]
    : LitigationPageTitle[
        pathname.replace(idPattern[0], ':caseId').replace(idPattern[1], ':id')
      ];
};

const PageWrapper = ({
  activeCase,
  activeArbitrator,
  activeArbitration,
  activeTab,
  children,
  closeTab,
  isSidebarVisible,
  location,
  setActiveTab,
  toggleSidebar,
  userRights,
  userStatus,
  quickCaseLookup,
  history,
  resetActiveCase,
  resetActiveArbitrator,
  resetActiveArbitration,
}) => {
  const [forceRender, setForceRender] = useState(false);
  const isReadOnly = userStatus.get('readOnly');
  const canView = canViewSubject('Litigation');
  useEffect(() => {
    if (userRights && userRights.toJS() && userRights.toJS().length) {
      const rules = defineRules(userRights.toJS());
      ability.update(rules);
      setForceRender(!forceRender);
    }
  }, [userRights]);

  useEffect(() => {
    const isiPad = navigator.userAgent.match(/Mac/) && navigator.maxTouchPoints;
    if (isSidebarVisible && isiPad) {
      toggleSidebar();
    }
  }, [location.pathname]);
  if(!canView) {
    return (
    <div>
      <Helmet>
        <title>Litigation</title>
      </Helmet>
      <Sidebar
        isOpen={isSidebarVisible}
        logo={litigationLogo}
        sidebarFooter={
          <SidebarFooter>
            <SidebarRdsNameWidget />
            <SidebarCaseSettingWidget />
            <SidebarAccountSettingsWidget />
          </SidebarFooter>
        }
      >
        <SidebarItem icon={rocketIcon} link="/" title="Prodigy App Launcher" />

        <SidebarTabs
          indicatorColor="secondary"
          scrollButtons="off"
          textColor="primary"
          value={activeTab}
          variant="fullWidth"
          onChange={setActiveTab}
        >
          <SidebarTab
            label={
              <SidebarTabLabel isActive={activeTab === 0} label="Litigation" />
            }
          />
        </SidebarTabs>
        {activeTab === 0 && (
          <Fragment>
            <SidebarItem link={LitigationRoute.Dashboard} title="Litigation Dashboard" />
            <SidebarItem nested title="Search">
              <SidebarItem
                link={LitigationRoute.LitigationCaseSearch}
                title="Litigation"
              />
              <SidebarItem
                link={LitigationRoute.OfferSearch}
                title="Litigation Offers"
              />
              {
                canViewSubject('Arbitration') ? (
                  <SidebarItem
                    link={LitigationRoute.ArbitrationCaseSearch}
                    title="Arbitration"
                  />
                ) : <></>
              }
              <SidebarItem link={LitigationRoute.SOAHCaseSearch} title="SOAH" />
              <SidebarItem
                link={LitigationRoute.PartyCaseSearch}
                title="Party"
              />
              <SidebarItem link={LitigationRoute.FirmSearch} title="Firm" />
              <SidebarItem
                link={LitigationRoute.ArbitratorSearch}
                title="Arbitrators"
              />
            </SidebarItem>
            <SidebarItem
              link={LitigationRoute.AppFunctions}
              title="App Functions"
            />
            <SidebarItem nested title="App Settings">
              <SidebarItem
                link={LitigationRoute.CodeFileMaintenance}
                title="Code File Maintenance"
              />
              {userRights
                .toJS()
                .includes('LitigationConfigurationMaintenance') ? (
                <SidebarItem
                  link={LitigationRoute.Configuration}
                  title="Configuration"
                />
              ) : (
                <></>
              )}
            </SidebarItem>
          </Fragment>
        )}
      </Sidebar>
      <PageWrapper.ContentWrapper>
        <SidebarPageWrapper isSidebarVisible={isSidebarVisible}>
          <HeaderWrapper>
            <Header
              title={parseHeaderTitle(location.pathname)}
              toggleSidebar={toggleSidebar}
            >
              <HeaderItem
                icon={screenIcon}
                title="Print Screen"
                onClick={() => window.print()}
              />
              <HeaderItem icon={docIcon} title="Audit Trail" />
              <HeaderZendeskWidget />
            </Header>
          </HeaderWrapper>

          <PageWrapper.NoUserRights>You must have the View or Edit Litigation user rights to view this page.</PageWrapper.NoUserRights>
        </SidebarPageWrapper>
      </PageWrapper.ContentWrapper>
    </div>
    );
  }
  return (
    <div>
      <Helmet>
        <title>Litigation</title>
      </Helmet>
      <QuickAccessCase quickCaseLookup={quickCaseLookup} />
      <Sidebar
        isOpen={isSidebarVisible}
        logo={litigationLogo}
        sidebarFooter={
          <SidebarFooter>
            <SidebarRdsNameWidget />
            <SidebarCaseSettingWidget />
            <SidebarAccountSettingsWidget />
          </SidebarFooter>
        }
      >
        <SidebarItem icon={rocketIcon} link="/" title="Prodigy App Launcher" />

        <SidebarTabs
          indicatorColor="secondary"
          scrollButtons="off"
          textColor="primary"
          value={activeTab}
          variant="fullWidth"
          onChange={setActiveTab}
        >
          <SidebarTab
            label={
              <SidebarTabLabel isActive={activeTab === 0} label="Litigation" />
            }
          />
          {activeCase && (
            <SidebarTab
              label={
                <SidebarTabLabel
                  hasClose
                  isActive={activeTab === 1}
                  label="Case"
                  onClose={event => {
                    event.stopPropagation();
                    resetActiveCase();
                    closeTab();
                    history.push('/litigation/litigation-search');
                  }}
                />
              }
            />
          )}
          {activeArbitration && (
            <SidebarTab
              label={
                <SidebarTabLabel
                  hasClose
                  isActive={activeTab === 1}
                  label="Arbitration"
                  onClose={event => {
                    event.stopPropagation();
                    resetActiveArbitration();
                    closeTab();
                    history.push('/litigation/arbitration-search');
                  }}
                />
              }
            />
          )}
          {activeArbitrator && (
            <SidebarTab
              label={
                <SidebarTabLabel
                  hasClose
                  isActive={activeTab === 1}
                  label="Arbitrator"
                  onClose={event => {
                    event.stopPropagation();
                    resetActiveArbitrator();
                    closeTab();
                    history.push('/litigation/arbitrator-search');
                  }}
                />
              }
            />
          )}
        </SidebarTabs>
        {activeTab === 0 && (
          <Fragment>
            {/* <SidebarItem nested title="Search">
              <SidebarItem
                link={LitigationRoute.LitigationCaseSearch}
                title="Litigation"
              />
              <SidebarItem
                link={LitigationRoute.OfferSearch}
                title="Litigation Offers"
              />
              {
                canViewSubject('Arbitration') ? (
                  <SidebarItem
                    link={LitigationRoute.ArbitrationCaseSearch}
                    title="Arbitration"
                  />
                ) : <></>
              }
              <SidebarItem link={LitigationRoute.SOAHCaseSearch} title="SOAH" />
              <SidebarItem
                link={LitigationRoute.ArbitratorSearch}
                title="Arbitrators"
              />
            </SidebarItem> */}
            <SidebarItem nested title="Litigation" isOpen>
              <SidebarItem link={LitigationRoute.Dashboard} title="Dashboard" />
              <SidebarItem
                link={LitigationRoute.LitigationCaseSearch}
                title="Case Search"
              />
              <SidebarItem
                link={LitigationRoute.MyLitigationCases}
                title="My Cases"
              />
              <SidebarItem
                link={LitigationRoute.OfferSearch}
                title="Offers"
              />
              <SidebarItem
                link={LitigationRoute.PartyCaseSearch}
                title="Party"
              />
              <SidebarItem link={LitigationRoute.FirmSearch} title="Firm" />
            </SidebarItem>
            <SidebarItem nested title="Arbitration" isOpen>
              {/* <SidebarItem link={LitigationRoute.Dashboard} title="Dashboard" /> */}
              {
                canViewSubject('Arbitration') ? (
                  <SidebarItem
                    link={LitigationRoute.ArbitrationCaseSearch}
                    title="Case Search"
                  />
                ) : <></>
              }
              <SidebarItem
                link={LitigationRoute.MyArbitrationCases}
                title="My Arbitration Cases"
              />
              <SidebarItem
                link={LitigationRoute.ArbitratorSearch}
                title="Arbitrators"
              />
            </SidebarItem>
            <SidebarItem nested title="SOAH" isOpen>
              <SidebarItem link={LitigationRoute.SOAHCaseSearch} title="Case Search" />
            </SidebarItem>
            <SidebarItem
              link={LitigationRoute.AppFunctions}
              title="App Functions"
            />
            <SidebarItem nested title="App Settings">
              <SidebarItem
                link={LitigationRoute.CodeFileMaintenance}
                title="Code File Maintenance"
              />
              {userRights
                .toJS()
                .includes('LitigationConfigurationMaintenance') ? (
                <SidebarItem
                  link={LitigationRoute.Configuration}
                  title="Configuration"
                />
              ) : (
                <></>
              )}
            </SidebarItem>
          </Fragment>
        )}
        {activeTab === 1 && activeCase && (
          <Fragment>
            <SidebarItem isOpen nested title="Litigation Case">
              <SidebarItem
                link={`/litigation/litigation-case-maintenance/${activeCase}`}
                title="Case Maintenance"
              />
              <SidebarItem
                link={`/litigation/litigation-motions-discovery/${activeCase}`}
                title="Motions & Discovery"
              />
              <SidebarItem
                link={`/litigation/litigation-offers/${activeCase}`}
                title="Offers"
              />
              <SidebarItem
                link={`/litigation/litigation-case-events/${activeCase}`}
                title="Case Events"
              />
              <SidebarItem
                link={`/litigation/litigation-cad-communication/${activeCase}`}
                title="CAD Communications"
              />
              <SidebarItem
                link={`/litigation/litigation-contact-information/${activeCase}`}
                title="Parties"
              />
              <SidebarItem
                link={`/litigation/litigation-activity-log/${activeCase}`}
                title="Activity Log"
              />
              <SidebarItem
                link={`/litigation/litigation-revision-history/${activeCase}`}
                title="Revision History"
              />
            </SidebarItem>
          </Fragment>
        )}
        {activeTab === 1 && activeArbitration && (
          <Fragment>
            <SidebarItem isOpen nested title="Arbitration">
              <SidebarItem
                link={`/litigation/arbitration/${activeArbitration}`}
                title="General"
              />
              <SidebarItem
                link={`/litigation/arbitration/${activeArbitration}/caseprep`}
                title="Case Prep"
              />
              <SidebarItem
                link={`/litigation/arbitration/${activeArbitration}/documents-evidence`}
                title="Documents/Evidence"
              />
              <SidebarItem
                link={`/litigation/arbitration/${activeArbitration}/hearing`}
                title="Hearing"
              />
              <SidebarItem
                link={`/litigation/arbitration/${activeArbitration}/disposition`}
                title="Disposition"
              />
              <SidebarItem
                link={`/litigation/arbitration/${activeArbitration}/comments`}
                title="Comments"
              />
              <SidebarItem
                link={`/litigation/arbitration/${activeArbitration}/events`}
                title="Events"
              />
              <SidebarItem
                link={`/litigation/arbitration/${activeArbitration}/revision-history`}
                title="Revision History"
              />
            </SidebarItem>
          </Fragment>
        )}
      </Sidebar>
      <PageWrapper.ContentWrapper>
        <SidebarPageWrapper isSidebarVisible={isSidebarVisible}>
          <HeaderWrapper>
            <Header
              title={parseHeaderTitle(location.pathname)}
              toggleSidebar={toggleSidebar}
            >
              {!isReadOnly && (
                <HeaderItem
                  icon={partyIcon}
                  link="/litigation/litigation-party-maintenance/create"
                  title="Add Party"
                />
              )}
              {!isReadOnly && (
                <HeaderItem
                  icon={generateIcon}
                  link="/litigation/litigation-case-maintenance/create"
                  title="Add Case"
                />
              )}

              <HeaderItem
                icon={screenIcon}
                title="Print Screen"
                onClick={() => window.print()}
              />
              <HeaderItem icon={docIcon} title="Audit Trail" />
              <HeaderZendeskWidget />
            </Header>
          </HeaderWrapper>

          <div>{children}</div>
        </SidebarPageWrapper>
        <PropertyLookupModal />
        <ArbitratorLookupModal />
        <ArbitratorCreateModal />
        <OwnerLookupModal />
        <OwnerCreateModal />
      </PageWrapper.ContentWrapper>
    </div>
  );
};

PageWrapper.propTypes = {
  activeCase: PropTypes.string,
  activeArbitrator: PropTypes.string,
  activeArbitration: PropTypes.string,
  activeTab: PropTypes.number.isRequired,
  children: PropTypes.node,
  closeTab: PropTypes.func.isRequired,
  isSidebarVisible: PropTypes.bool,
  location: PropTypes.object.isRequired,
  resetActiveCase: PropTypes.func.isRequired,
  resetActiveArbitrator: PropTypes.func.isRequired,
  resetActiveArbitration: PropTypes.func.isRequired,
  setActiveTab: PropTypes.func.isRequired,
  toggleSidebar: PropTypes.func.isRequired,
  userRights: PropTypes.any,
  userStatus: PropTypes.any,
  quickCaseLookup: PropTypes.func.isRequired,
  history: PropTypes.object.isRequired,
};

PageWrapper.ContentWrapper = styled.div`
  > div {
    margin-top: -67px;
    min-height: 100vh;
    background-color: #f2f4f6;
  }
`;

PageWrapper.NoUserRights = styled.div`
  display: flex;
  justify-content: center;
  padding: 50px;
  font-size: 20px;
`;

const mapStateToProps = state => ({
  userRights: userRightsSelector(state),
  activeCase: activeCaseSelector(state),
  activeArbitrator: activeArbitratorSelector(state),
  activeArbitration: activeArbitrationSelector(state),
  isSidebarVisible: isSidebarVisibleSelector(state),
  userStatus: userStatusSelector(state),
});

const mapDispatchToProps = {
  resetActiveCase: caseActions.resetActiveCase,
  resetActiveArbitrator: arbitratorActions.resetActiveArbitrator,
  resetActiveArbitration: arbitrationActions.resetActiveArbitration,
  toggleSidebar: globalActions.toggleSidebar,
  fetchCurrentAppraisalYear: fetchCurrentAppraisalYear,
  quickCaseLookup: caseActions.quickCaseLookup,
};

const enhance = compose(
  withRouter,
  connect(
    mapStateToProps,
    mapDispatchToProps,
  ),
  withStateHandlers(({ activeTab = 0 }) => ({ activeTab }), {
    setActiveTab: () => (_, activeTab) => ({ activeTab }),
    closeTab: (_, { resetActiveCase }) => () => {
      resetActiveCase();
      return { activeTab: 0 };
    },
  }),
  lifecycle({
    componentDidUpdate(prevProps) {
      if (
        (this.props.activeCase !== prevProps.activeCase &&
          this.props.activeCase) ||
        (this.props.activeArbitrator !== prevProps.activeArbitrator &&
          this.props.activeArbitrator) ||
        (this.props.activeArbitration !== prevProps.activeArbitration &&
          this.props.activeArbitration)
      ) {
        this.props.setActiveTab(null, 1);
      }
    },
  }),
);

export default enhance(PageWrapper);
